/**
 * The new task's name in the routing panel, and whether Roger filled it in.
 *
 * An agency can name the task a fax type is filed to. That name prefills the
 * field, but never over a name the user typed, and with no agency name the
 * field is left exactly as it was. Every function is meant for a functional
 * state update (`setTaskName(prev => ...)`), so none reads a stale value.
 */
export interface TaskNameState {
  name: string;
  /** True while the name is still the one Roger suggested. */
  fromSuggestion: boolean;
}

export const EMPTY_TASK_NAME: TaskNameState = {
  name: '',
  fromSuggestion: false,
};

/** A new suggestion arrived. An empty name counts as no name. */
export function afterSuggestion(
  state: TaskNameState,
  suggestedName: string | null
): TaskNameState {
  if (!suggestedName) {
    return state.fromSuggestion ? EMPTY_TASK_NAME : state;
  }
  if (state.name === '' || state.fromSuggestion) {
    return { name: suggestedName, fromSuggestion: true };
  }
  return state;
}

/**
 * The user picked another task type. A suggested name fits only the suggested
 * type, so it clears on the way off and comes back on the way back, unless the
 * user has typed a name of their own.
 */
export function afterTypeChange(
  state: TaskNameState,
  newType: string,
  suggestedType: string,
  suggestedName: string | null
): TaskNameState {
  if (newType !== suggestedType) {
    return state.fromSuggestion ? EMPTY_TASK_NAME : state;
  }
  if (suggestedName && state.name === '') {
    return { name: suggestedName, fromSuggestion: true };
  }
  return state;
}

/** The user typed: the name is theirs now. */
export function afterTyping(name: string): TaskNameState {
  return { name, fromSuggestion: false };
}
